<template>
  <div class="catalog-view">
    <page-header
      v-if="showHeader"
      :breadcrumbs="catalogBreadcrumbs"
    >
      <template v-slot:topbar>
        <h1>Catalog</h1>
      </template>
    </page-header>
    <div class="catalog-view-content-wrapper">
      <div
        v-if="recommendedCatalogPage"
        class="recommended-catalog-page"
      >
        <h3 class="all-upper-case">
          {{ 'Recommended Add-On' }}
        </h3>
        <div class="recommended-image-container">
          <div class="hero-wrapper">
            <button
              class="recommended-hero-image"
              :style="{backgroundImage: `url(${getAttachmentUrl(recommendedCatalogPage.heroImageId)})`}"
              @click="routeToPage(recommendedCatalogPage)"
            />
          </div>
          <div class="recommended-info">
            <button
              class="page-icon"
              :style="{backgroundImage: `url(${getAttachmentUrl(recommendedCatalogPage.iconId)})`}"
              @click="routeToPage(recommendedCatalogPage)"
            />
            <h2 @click="routeToPage(recommendedCatalogPage)">
              {{ recommendedCatalogPage.title }}
            </h2>
            <p>{{ stripHtml(recommendedCatalogPage.shortDescription) }}</p>
            <action-button
              v-if="showActionButton(recommendedCatalogPage)"
              :addon-status="recommendedCatalogPage.catalogPageStatus($store)"
              :catalog-page="recommendedCatalogPage"
              @handle-action-button="handleActionButton(recommendedCatalogPage)"
            />
          </div>
        </div>
      </div>
      <div
        v-for="section in (catalog && catalog.orderedSections)"
        :key="section.id"
        class="catalog-sections"
      >
        <div
          v-if="getSectionCatalogPages(section.id).length"
          class="catalog-section"
        >
          <h3 class="all-upper-case">
            {{ section.title }}
          </h3>
          <div class="catalog-pages">
            <div
              v-for="page in getSectionCatalogPages(section.id)"
              :key="page.id"
              class="catalog-page"
            >
              <div class="page-top-bar">
                <button
                  class="page-icon"
                  :style="{backgroundImage: `url(${getAttachmentUrl(page.iconId)})`}"
                  @click="routeToPage(page)"
                />
                <h2 @click="routeToPage(page)">
                  {{ page.title }}
                </h2>
              </div>
              <p>{{ stripHtml(page.shortDescription) }}</p>
              <action-button
                v-if="showActionButton(page)"
                :addon-status="page.catalogPageStatus($store)"
                :catalog-page="page"
                dark
                @handle-action-button="handleActionButton(page)"
              ></action-button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <catalog-add-modal />
    <catalog-remove-modal />
  </div>
</template>

<script>
import ActionButton from '@/components/catalog/action-button.vue';
import CatalogAddModal from '@/components/catalog/add-modal.vue';
import CatalogRemoveModal from '@/components/catalog/remove-modal.vue';
import PageHeader from '@/components/singletons/pageHeader.vue';

import { stripHtml } from '@/utils';

import GlobalMixin from '@/mixins/global';

export default {
  name: 'CatalogView',
  components: { ActionButton, CatalogAddModal, CatalogRemoveModal, PageHeader },
  mixins: [GlobalMixin],
  props: {
    showHeader: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    catalog() {
      return this.$store.getters['catalog/get'];
    },
    catalogBreadcrumbs() {
      return [{ name: 'Catalog', route: { name: 'catalog#index' } }];
    },
    catalogPages() {
      return this.$store.getters['catalog/pageList'];
    },
    recommendedCatalogPage() {
      if (this.catalog?.recommendedCatalogPages) {
        const classificationId = this.$store.getters['user/get']?.classificationId;
        const matchingClassRecommendedPage = this.catalog.recommendedCatalogPages.find(rPage => rPage.classificationId === classificationId);

        if (matchingClassRecommendedPage && this.catalogPages?.length) {
          return this.catalogPages.find(page => page.id === matchingClassRecommendedPage?.catalogPageId);
        }
      }
    },
    navigationGroup() {
      const id = Number(this.$route.params.navigationGroupId);
      return this.$store.getters['navigationGroups/navigationGroups'][id];
    },
  },
  created() {
    if (!this.catalog) {
      this.$router.push({ name: 'mastery#home' });
    }
    const attachmentIds = [];
    for (const page of (this.catalogPages || [])) {
      attachmentIds.push(page.heroImageId);
      attachmentIds.push(page.iconId);
      attachmentIds.push(page.addConfig?.iconId);
      attachmentIds.push(page.removeConfig?.iconId);
      for (const marquee of page.marquees) {
        attachmentIds.push(marquee.iconId);
        attachmentIds.push(marquee.imageId);
      }
    }
    if (attachmentIds.length > 0) {
      this.$store.dispatch('attachments/fetch', { ids: attachmentIds });
    }
  },
  methods: {
    getAttachmentUrl(attachmentId) {
      return this.$store.getters['attachments/get'][attachmentId]?.url;
    },
    getSectionCatalogPages(sectionId) {
      const sectionCatalogPages = [];
      if (this.catalogPages?.length) {
        this.catalogPages.forEach(page => {
          if (page.catalogSectionIds?.includes(sectionId)) {
            sectionCatalogPages.push(page);
          }
        });
      }
      return sectionCatalogPages;
    },
    handleActionButton(catalogPage) {
      const source = { type: 'Catalog Page', id: catalogPage.id, name: catalogPage.title };
      if (catalogPage.isAddonActive(this.$store)) {
        this.$router.push({
          name: 'dashboard#group',
          params: {
            dashboardId: catalogPage.getDashboardId(this.$store),
            navigationGroupId: this.navigationGroup.id,
          },
          query: this.currentQuery,
        });
        this.$analytics.events.viewedCatalogPage(catalogPage, {
          id: catalogPage.id,
          type: 'Catalog Home Page',
          name: 'Open Button',
        });
      } else {
        this.$modal.show('catalog-add-modal', { catalogPage, source });
        this.$analytics.events.selectedAddOn(catalogPage, { id: this.catalog.id, type: 'Catalog Home Page' });
      }
    },
    routeToPage(page) {
      this.$router.push({
        name: 'catalog#page#show',
        params: {
          catalogPageId: page.id,
        },
        query: this.currentQuery,
      });
    },
    showActionButton(catalogPage) {
      const user = this.$store.getters['user/get'];
      if (user?.previewer) {
        return true;
      }
      if (!catalogPage.purchaseEnabled) {
        return false;
      }
      return true;
    },
    stripHtml,
  },
};
</script>

<style lang="scss" scoped>
.catalog-view {
  display: flex;
  flex-direction: column;
  align-items: center;

  .all-upper-case {
    text-transform: uppercase;
  }

  .catalog-view-content-wrapper {
    max-width: rem-calc(768);
    width: 100%;

    h3 {
      margin-bottom: rem-calc(24);
      font-size: 13px;
      font-weight: $bold;
      letter-spacing: .5px;
      color: #767676;
    }

    .page-icon {
      min-width: rem-calc(50);
      max-width: rem-calc(50);
      min-height: rem-calc(50);
      background: transparent;
      background-position: center;
      background-size: cover;
      object-fit: cover;
      object-position: center;
    }

    .recommended-catalog-page {

      .recommended-image-container {
        display: flex;
        height: rem-calc(320);
        border-radius: 8px;

        .hero-wrapper {
          flex: 1;
          height: rem-calc(320);

          .recommended-hero-image {
            width: 100%;
            height: 100%;
            background-position: center;
            background-size: cover;
            border-radius: 8px 0 0 8px;
            object-fit: cover;
            object-position: center;
          }
        }
        .recommended-info {
          width: 15rem;
          height: rem-calc(272);
          display: flex;
          flex-direction: column;
          padding: rem-calc(24);
          border-radius: 0 8px 8px 0;
          background-color: #001c74;
          color: #ffffff;
          h2 {
            margin: 1rem 0;
            cursor: pointer;
            font-size: rem-calc(24);
            @include charter-font;
          }
          p {
            margin: 0;
            font-size: 1rem;
            line-height: 1.5;
          }
        }
        /deep/ .catalog-action-button-component {
          margin-top: auto;
        }
      }

      @include breakpoint(small) {
        .recommended-image-container {
          height: auto;
          display: flex;
          flex-direction: column;

          .hero-wrapper {
            .recommended-hero-image {
              height: 100%;
              min-height: rem-calc(150);
              border-radius: 8px 8px 0 0;
            }
          }
          .recommended-info {
            height: auto;
            width: inherit;
            border-radius: 0 0 8px 8px;
            p {
              margin-bottom: 1rem;
            }
          }
        }
      }
    }
    .catalog-sections {
      .catalog-section {
        padding: 3rem 0;
        border-bottom: solid 1px #d9d9d9;

        .catalog-pages {
          display: flex;
          flex-wrap: wrap;
          justify-content: space-between;

          .catalog-page {
            width: calc(50% - 2rem);
            margin-bottom: 1rem;

            .page-top-bar{
              display: flex;
              align-items: center;

              h2 {
                margin-left: 1rem;
                cursor: pointer;
                font-size: rem-calc(24);
                @include charter-font;
              }
            }
            p {
              line-height: 1.5;
              margin-bottom: 2rem;
            }
          }
        }
        @include breakpoint(small) {
          .catalog-pages {
            flex-direction: column;
            justify-content: flex-start;

            .catalog-page {
              width: 100%;
              &:not(:first-child) {
                margin-top: 1rem;
              }
            }
          }
        }
      }
      &:last-child {
        .catalog-section {
          border: none;
          padding-bottom: 0;
        }
      }
    }
  }
}
</style>
